/**
 * D-274b (D-274 §3) — "ישירות תחת OTE": the engagement / project that holds
 * the tasks created "directly under OTE" is a behind-the-scenes container
 * ("עבודה פנימית"); the user never sees its name. Pure (no React): pinned by
 * `utils/__tests__/d274b-direct-under-ote.test.ts`.
 */

/** Is this the hidden container? (by its engagement id, or by the server's flag). */
export function isDirectUnderOte(
  row: { engagement_id?: string | null; direct_under_ote?: boolean | null },
  internalEngagementId: string | null | undefined
): boolean {
  if (row.direct_under_ote === true) return true;
  return !!internalEngagementId && !!row.engagement_id && row.engagement_id === internalEngagementId;
}

/** The "project / engagement" cell of a task list: the label for the hidden container, else as before. */
export function taskListContainerText(
  task: { engagement_id?: string | null; direct_under_ote?: boolean | null; project_name?: string | null; engagement_name?: string | null },
  internalEngagementId: string | null | undefined,
  directLabel: string
): string | null {
  if (isDirectUnderOte(task, internalEngagementId)) return directLabel;
  return task.project_name || task.engagement_name || null;
}

/** The "type" cell of a task list: "עבודה פנימית" for any task of the company, else the engagement type as before. */
export function taskTypeText(
  task: { engagement_id?: string | null; direct_under_ote?: boolean | null; organization_id?: string | null; engagement_type?: string | null },
  internal: { internalOrgId?: string | null; internalEngagementId?: string | null },
  internalLabel: string,
  typeLabel: (type: string) => string
): string | null {
  if (isDirectUnderOte(task, internal.internalEngagementId)) return internalLabel;
  if (internal.internalOrgId && task.organization_id === internal.internalOrgId) return internalLabel;
  return task.engagement_type ? typeLabel(task.engagement_type) : null;
}

/** "עבודה פנימית OTE › ישירות תחת OTE" (instead of "OTE › עבודה פנימית"); other rows: customer › container › milestone. */
export function startablePathText(
  task: { organization_name: string | null; container_name: string | null; milestone_name: string | null; direct_under_ote?: boolean | null },
  labels: { company: string; direct: string }
): string {
  if (task.direct_under_ote === true) return [labels.company, labels.direct, task.milestone_name].filter(Boolean).join(' › ');
  return [task.organization_name, task.container_name, task.milestone_name].filter(Boolean).join(' › ');
}

/**
 * The "לשייך גם?" check (D-59) never names the hidden "עבודה פנימית" project:
 * when the task's project is that container, the project is dropped from the
 * check (a milestone, if any, is still asked about); no one is missing → no question.
 */
export function withoutHiddenProject<C extends {
  milestone: { id: string; name: string } | null;
  project: { id: string; name: string } | null;
  agents: Array<{ in_milestone: boolean; in_project: boolean }>;
  ask: boolean;
}>(check: C, hiddenProjectId: string | null | undefined): C {
  if (!hiddenProjectId || !check.project || check.project.id !== hiddenProjectId) return check;
  const ask = check.agents.some((a) => !!check.milestone && !a.in_milestone);
  return { ...check, project: null, ask };
}
